import { type Pronoun } from 'domain/pronouns';

export type BackgroundColors = 'grey' | 'white';

export type Chapter = {
  id: Chapters;
  titleKey: string;
  productTypes: LicensedProduct[];
  mandatory: boolean;
};

export type ChapterPositions = Record<string, number>;

export type Chapters =
  | 'FOUNDATION'
  | 'MANAGEMENT'
  | 'EFFECTIVE_SELLING'
  | 'PERSONAL_ACHIEVEMENT'
  | 'CRITICAL_THINKING'
  | 'INTERVIEW'
  | 'IDTL'
  | 'DFC'
  | 'IMPACT';

export type ChapterTypes = Partial<Record<Chapters, string>>;

export type ColourEnergy = Record<JungianColours, string>;

export type DatadogRumConfig = {
  hostname: string;
  applicationId: string;
  clientToken: string;
};

export type DotType = '4-COLOUR' | '8-COLOUR';

export type EventType =
  | 'INSIGHTS_DISCOVERY_WORKSHOP'
  | 'INSIGHTS_DISCOVERY_ACCREDITATION'
  | 'INSIGHTS_SELF_AWARE_LEADER'
  | 'INSIGHTS_DISCOVERING_LEADERSHIP_EFFECTIVENESS'
  | 'INSIGHTS_DISCOVERY_FULL_CIRCLE'
  | 'INSIGHTS_EXPLORE'
  | 'INSIGHTS_CRITICAL_THINKING';

export type EventTypes = Record<
  EventType,
  {
    textKey: string;
    color: InsightsColors;
  }
>;

export type Frequency = 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'NEVER';

export type FrequencyType = {
  textKey: string;
  value: Frequency;
};

export type InsightsColors =
  'blue' | 'orange' | 'green' | 'darkGreen' | 'pink' | 'red' | 'grey';

export type InviteeListOptions =
  | 'ADDED'
  | 'INVITE_SENT'
  | 'INVITE_REQUESTED'
  | 'READY_TO_PURCHASE'
  | 'PROFILE_GENERATED';

export type InviteeOptions = Record<
  Exclude<InviteeListOptions, 'INVITE_REQUESTED'>,
  {
    textKey: string;
    color: InsightsColors;
  }
>;
export type InviteeStatuses = Record<
  InviteeListOptions,
  {
    textKey: string;
    color: InsightsColors;
  }
>;

export type SimplifiedJungianColours = 'blue' | 'green' | 'yellow' | 'red';

export type InviteeColorScores = {
  [colour in SimplifiedJungianColours]: number;
} & {
  preferred: SimplifiedJungianColours[];
};

export type JungianColours =
  'CoolBlue' | 'EarthGreen' | 'SunshineYellow' | 'FieryRed';

export const LICENSED_PRODUCTS_ARRAY = [
  'DISCOVERY_SELF_AWARE_PROFILE',
  'DISCOVERY_PERSONAL_PROFILE',
  'DISCOVERY_TRANSFORMATIONAL_LEADERSHIP_PROFILE',
  'DISCOVERY_FULL_CIRCLE_PROFILE',
  'DISCOVERY_EXPLORE_PROFILE',
  'DISCOVERY_IMPACT_PROFILE',
] as const;

export type LicensedProductOption = Record<
  LicensedProduct,
  Option<LicensedProduct>
>;

export type Option<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
  defaultSelected?: boolean;
};

export type Options = Option<string>[];

export type LicensedProduct = (typeof LICENSED_PRODUCTS_ARRAY)[number];

export type LicensedProducts = Record<LicensedProduct, string>;

export type ProductTypes = Record<LicensedProduct, string>;

export type ProfileType =
  | 'DISCOVERY_PERSONAL_PROFILE'
  | 'DISCOVERY_PROFILE'
  | 'IDTL_PROFILE'
  | 'DFC_PROFILE'
  | 'EXPLORE_PROFILE'
  | 'API_PROFILE'
  | 'IMPACT_PROFILE';

export type ProfileTypes = Record<ProfileType, string>;

export type Role =
  | 'SUPER_ADMIN'
  | 'GROUP_MANAGER'
  | 'ADVANCED_USER'
  | 'STANDARD'
  | 'RESTRICTED_USER';

export type Roles = Record<Role, string>;

export type RolePositions = Record<string, number>;

export type ScoresGroupingType = 'AGGREGATED' | 'DISTINCT';

export type ScoreType = 'CONSCIOUS' | 'LESS_CONSCIOUS';

export enum SortDirection {
  ASCENDING = 'ASCENDING',
  DESCENDING = 'DESCENDING',
}

export enum SortType {
  NUMBER = 'NUMBER',
  NAME = 'NAME',
  WHEEL_POSITION = 'WHEEL_POSITION',
  COLOUR_PREF = 'COLOUR_PREF',
  ORGANISATION = 'ORGANISATION',
}

export type User = {
  fullName: string;
  emailAddress: string;
  id: string;
  role?: Role;
};

export type Evaluator =
  'DISCOVERY' | 'DDDRS' | 'DFC' | 'EXPLORE' | 'CLASSIC_EXPLORE';

export interface HasStatus {
  status: InviteeListOptions;
}

export interface Contributor extends HasStatus {
  id: string;
  email: string;
  fullName: string;
  status: InviteeListOptions;
  pronoun?: Pronoun;
  discoveryProfile?: null | {
    createdAt: string;
  };
  evaluation?: { submittedOn: string | undefined };
}

export type EvaluatorLinksSortCategory =
  'DATE_CREATED' | 'MOST_COMPLETED' | 'LEAST_COMPLETED';

export enum InviteUserOrgOptions {
  ONE_ORG = 'ONE_ORG',
  MULTI_ORG = 'MULTI_ORG',
}

export type AnonymisationType = 'NAMED' | 'ANONYMISED';

type NthTupleOfLength<
  N extends number,
  T,
  R extends unknown[],
> = R['length'] extends N ? R : NthTupleOfLength<N, T, [T, ...R]>;

export type TupleOfLength<N extends number, T> = N extends N
  ? number extends N
    ? T[]
    : NthTupleOfLength<N, T, []>
  : never;
